import { ValidationErrors } from '@angular/forms';
import { isNullOrUndefined } from '@mvneco/eb-core';

/**
 * Value validators
 * Similar to Angular form/Validators, but not operating on AbstractControls but on values.
 * Building block for complex validators
 */

/**
 * please note: The validators have been extracted from Angular source code. Therefore the Copyright:
 *
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

const EMAIL_REGEXP =
    /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

export function isEmptyInputValue(value: any): boolean {
    // we don't check for string here so it also works with arrays
    return value == null || value.length === 0;
}

export class ValueValidators {
    static min(controlValue: string, min: number, prefix?: string): ValidationErrors | null {
        const pf = prefix || '';
        if (isEmptyInputValue(controlValue) || isEmptyInputValue(min)) {
            return null;  // don't validate empty values to allow optional controls
        }
        const value = parseFloat(controlValue);
        // Controls with NaN values after parsing should be treated as not having a
        // minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
        return !isNaN(value) && value < min ? { [pf + 'min']: { min, actual: controlValue } } : null;
    }

    static max(controlValue: string, max: number, prefix?: string): ValidationErrors | null {
        const pf = prefix || '';
        if (isEmptyInputValue(controlValue) || isEmptyInputValue(max)) {
            return null;  // don't validate empty values to allow optional controls
        }
        const value = parseFloat(controlValue);
        // Controls with NaN values after parsing should be treated as not having a
        // maximum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-max
        return !isNaN(value) && value > max ? { [pf + 'max']: { max, actual: controlValue } } : null;
    }

    static required(controlValue: any, prefix?: string): ValidationErrors | null {
        const pf = prefix || '';
        return isEmptyInputValue(controlValue) ? { [pf + 'required']: true } : null;
    }


    static requiredTrue(controlValue: boolean, prefix?: string): ValidationErrors | null {
        const pf = prefix || '';
        return controlValue === true ? null : { [pf + 'required']: true };
    }

    static email(controlValue: string, prefix?: string): ValidationErrors | null {
        const pf = prefix || '';
        if (isEmptyInputValue(controlValue)) {
            return null;  // don't validate empty values to allow optional controls
        }
        return EMAIL_REGEXP.test(controlValue) ? null : { [pf + 'email']: true };
    }

    static minLength(controlValue: string, minLength: number, prefix?: string): ValidationErrors | null {
        const pf = prefix || '';
        if (isEmptyInputValue(controlValue)) {
            return null;  // don't validate empty values to allow optional controls
        }
        const length: number = controlValue ? controlValue.length : 0;
        return length < minLength ?
            { [pf + 'minlength']: { requiredLength: minLength, actualLength: length } } :
            null;
    }

    static maxLength(controlValue: string, maxLength: number, prefix?: string): ValidationErrors | null {
        const pf = prefix || '';
        const length: number = controlValue ? controlValue.length : 0;
        return length > maxLength ?
            { [pf + 'maxlength']: { requiredLength: maxLength, actualLength: length } } :
            null;
    }

    static pattern(controlValue: string, pattern: string | RegExp, prefix?: string): ValidationErrors | null {
        const pf = prefix || '';
        if (!pattern) {
            return null;
        }
        let regex: RegExp;
        let regexStr: string;
        if (typeof pattern === 'string') {
            regexStr = '';

            if (pattern.charAt(0) !== '^') {
                regexStr += '^';
            }

            regexStr += pattern;

            if (pattern.charAt(pattern.length - 1) !== '$') {
                regexStr += '$';
            }

            regex = new RegExp(regexStr);
        } else {
            regexStr = pattern.toString();
            regex = pattern;
        }

        if (isEmptyInputValue(controlValue)) {
            return null;  // don't validate empty values to allow optional controls
        }
        const value: string = controlValue;
        return regex.test(value) ? null :
            { [pf + 'pattern']: { requiredPattern: regexStr, actualValue: value } };
    }

    static mergeErrors(arrayOfErrors: ValidationErrors[]): ValidationErrors | null {
        const res: { [key: string]: any } =
            arrayOfErrors.reduce((res2: ValidationErrors | null, errors: ValidationErrors | null) => {
                if (isNullOrUndefined(res2)) {
                    return errors != null ? { ...errors } : {};
                } else {
                    return errors != null ? { ...res2, ...errors } : res2;
                }
            }, {});
        return Object.keys(res).length === 0 ? null : res;
    }
}

export function mergeErrors(arrayOfErrors: ValidationErrors[]): ValidationErrors | null {
    const res: { [key: string]: any } =
        arrayOfErrors.reduce((res2: ValidationErrors | null, errors: ValidationErrors | null) => {
            if (isNullOrUndefined(res2)) {
                return errors != null ? { ...errors } : {};
            } else {
                return errors != null ? { ...res2, ...errors } : res2;
            }
        }, {});
    return Object.keys(res).length === 0 ? null : res;
}
